import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';

// A checkbox list behind a select-shaped button. Same footprint as the
// single-value <select> it replaces, but the value is a set — the Brands
// country filter needs it because watching, say, the US and Canada
// together is the normal case rather than the exception.
//
// The list expands inline rather than floating: the filters live in a
// card on desktop and in a scrolling sheet on a phone, and a popover
// would have to escape the overflow of both.

const STROKE = 2.75;
// Below this many options the search box is more clutter than help.
const SEARCH_FROM = 8;

function MultiSelect({
  options,
  selected,
  onChange,
  allLabel = 'All',
  summaryNoun = 'selected',
  searchPlaceholder = 'Search…',
  ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);

  const items = useMemo(
    () => options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o)),
    [options],
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? items.filter((i) => String(i.label).toLowerCase().includes(q)) : items;
  }, [items, query]);

  // Escape closes; a click elsewhere does too, so the list doesn't sit
  // open over the rest of the filters once the operator moves on.
  //
  // On `click`, not `mousedown`: collapsing the list on mousedown moves
  // everything below it up between press and release, so the press that
  // was aimed at Apply lands on nothing.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onClickAway = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('click', onClickAway);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onClickAway);
    };
  }, [open]);

  const toggle = (value) => {
    onChange(selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value]);
  };

  const summary = (() => {
    if (!selected.length) return allLabel;
    if (selected.length === 1) {
      const only = items.find((i) => i.value === selected[0]);
      return only ? only.label : selected[0];
    }
    return `${selected.length} ${summaryNoun}`;
  })();

  return (
    <div className="o-multi" ref={rootRef}>
      <button
        type="button"
        className="input o-multi-trigger"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={selected.length ? undefined : 'o-multi-placeholder'}>{summary}</span>
        <ChevronDown
          size={15}
          strokeWidth={STROKE}
          style={{ flex: 'none', transform: open ? 'rotate(180deg)' : undefined }}
        />
      </button>

      {open && (
        <div className="o-multi-panel" role="group" aria-label={ariaLabel}>
          {items.length >= SEARCH_FROM && (
            <div className="o-multi-search">
              <Search size={14} strokeWidth={STROKE} style={{ opacity: 0.55, flex: 'none' }} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
              />
            </div>
          )}

          <div className="o-multi-list">
            {matches.map((item) => {
              const on = selected.includes(item.value);
              return (
                <label key={item.value} className="o-multi-option">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggle(item.value)}
                  />
                  <span className="o-multi-box" aria-hidden="true">
                    {on && <Check size={11} strokeWidth={3.25} />}
                  </span>
                  <span className="o-multi-label">{item.label}</span>
                </label>
              );
            })}
            {!matches.length && (
              <p className="o-multi-empty">
                {items.length ? 'No match.' : 'Nothing to filter on yet.'}
              </p>
            )}
          </div>

          {selected.length > 0 && (
            <button type="button" className="btn btn-ghost o-multi-clear" onClick={() => onChange([])}>
              Clear selection
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default MultiSelect;
